// Outstanding requirements checklist for a single client. Lives in
// the client edit modal as its own section.
//
// What it does:
//   - Lists every row in public.client_requirements for this client
//   - Quick-add from a dropdown of common kinds + a custom-text path
//   - Mark complete / waived / re-open (status enum)
//   - Toggle ai_followup_enabled per row to silence noisy items
//     without losing the checklist entry
//   - Shows last reminder timestamp + count so the operator knows
//     when the AI last touched the client
//
// The cron at /api/workflow/scan-requirements does the actual
// reminder send — this component just manages the data the cron
// reads.

import React from 'react';
import { supabase } from '../lib/supabase';
import { Plus, Check, X, Trash2, RotateCcw, BellOff, Bell } from 'lucide-react';

const COMMON_KINDS = [
  { kind: 'signed_application',     label: 'Signed application' },
  { kind: 'voided_check',           label: 'Voided check / banking info' },
  { kind: 'medical_exam',           label: 'Medical exam' },
  { kind: 'beneficiary_form',       label: 'Beneficiary form' },
  { kind: 'contingent_beneficiary', label: 'Contingent beneficiary' },
  { kind: 'drivers_license',        label: "Driver's license copy" },
  { kind: 'illustration',           label: 'Signed illustration' },
  { kind: 'replacement_form',       label: 'Replacement form' },
  { kind: 'premium_payment',        label: 'First premium payment' },
  { kind: 'hipaa_authorization',    label: 'HIPAA authorization' },
  { kind: 'health_questionnaire',   label: 'Health questionnaire' },
  { kind: 'parental_consent',       label: 'Parental consent (minor)' },
  { kind: 'other',                  label: 'Other (custom)' },
];

function fmtAgo(iso) {
  if (!iso) return '';
  const t = Date.parse(iso); if (!Number.isFinite(t)) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

export default function ClientRequirementsSection({ clientId, ownerId, showToast = () => {} }) {
  const [rows, setRows]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]       = React.useState(false);
  const [addKind, setAddKind]   = React.useState('');
  const [addCustom, setAddCustom] = React.useState('');
  const [addDueAt, setAddDueAt] = React.useState('');

  const load = React.useCallback(async () => {
    if (!clientId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from('client_requirements')
      .select('*')
      .eq('client_id', clientId)
      .order('status', { ascending: true })   // pending first
      .order('created_at', { ascending: true });
    if (error) { showToast(`Couldn't load requirements: ${error.message}`, 'error'); }
    setRows(data || []);
    setLoading(false);
  }, [clientId, showToast]);

  React.useEffect(() => { load(); }, [load]);

  const addRow = async () => {
    if (!clientId || !ownerId) { showToast('Save the client first, then add requirements.', 'info'); return; }
    if (!addKind) { showToast('Pick an item.', 'info'); return; }
    const kind = addKind;
    const description = kind === 'other' ? addCustom.trim() : null;
    if (kind === 'other' && !description) { showToast('Describe the custom item.', 'info'); return; }
    setBusy(true);
    const { error } = await supabase.from('client_requirements').insert({
      owner_id:  ownerId,
      client_id: clientId,
      kind,
      description,
      due_at:    addDueAt ? new Date(addDueAt).toISOString() : null,
    });
    setBusy(false);
    if (error) { showToast(`Add failed: ${error.message}`, 'error'); return; }
    setAddKind(''); setAddCustom(''); setAddDueAt('');
    await load();
  };

  // One-tap add for the 4 most common items. Skips the dropdown entirely.
  // No-ops if the item is already on file (pending OR completed) so a
  // double-click can't create duplicate checklist entries.
  const quickAdd = async (kind) => {
    if (!clientId || !ownerId) { showToast('Save the client first, then add requirements.', 'info'); return; }
    const existing = rows.find(r => r.kind === kind);
    if (existing) { showToast(`Already on file (status: ${existing.status}).`, 'info'); return; }
    setBusy(true);
    const { error } = await supabase.from('client_requirements').insert({
      owner_id:  ownerId,
      client_id: clientId,
      kind,
    });
    setBusy(false);
    if (error) { showToast(`Add failed: ${error.message}`, 'error'); return; }
    await load();
  };

  const setStatus = async (row, status) => {
    setBusy(true);
    const { error } = await supabase.from('client_requirements').update({ status }).eq('id', row.id);
    setBusy(false);
    if (error) { showToast(`Update failed: ${error.message}`, 'error'); return; }
    await load();
  };

  const toggleFollowup = async (row) => {
    setBusy(true);
    const { error } = await supabase.from('client_requirements')
      .update({ ai_followup_enabled: !row.ai_followup_enabled }).eq('id', row.id);
    setBusy(false);
    if (error) { showToast(`Update failed: ${error.message}`, 'error'); return; }
    await load();
  };

  const deleteRow = async (row) => {
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: `Delete this requirement (${labelFor(row)})?`,
      confirmLabel: 'Delete', danger: true,
    }) : Promise.resolve(window.confirm(`Delete this requirement (${labelFor(row)})?`)));
    if (!ok) return;
    setBusy(true);
    const { error } = await supabase.from('client_requirements').delete().eq('id', row.id);
    setBusy(false);
    if (error) { showToast(`Delete failed: ${error.message}`, 'error'); return; }
    await load();
  };

  const pending   = rows.filter(r => r.status === 'pending');
  const completed = rows.filter(r => r.status === 'completed' || r.status === 'waived');

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-bold text-sm">Outstanding requirements</h4>
          <p className="text-xs text-slate-500">AI Agent texts + emails the client automatically every 3 days while items stay pending. Creates a follow-up task for you if anything goes 7+ days unresolved.</p>
        </div>
        {pending.length > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
            {pending.length} pending
          </span>
        )}
      </div>

      {/* Quick-add chips — the four most common items, one tap each. */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Quick add:</span>
        {['signed_application', 'voided_check', 'medical_exam', 'beneficiary_form'].map(kind => {
          const meta = COMMON_KINDS.find(k => k.kind === kind);
          return (
            <button
              key={kind}
              onClick={() => quickAdd(kind)}
              disabled={busy || !clientId}
              className="text-xs font-semibold bg-white hover:bg-emerald-50 border border-slate-300 hover:border-emerald-300 text-slate-700 hover:text-emerald-800 rounded-full px-2.5 py-1 disabled:opacity-50"
              title={`Add "${meta.label}" to this client's checklist`}
            >
              + {meta.label}
            </button>
          );
        })}
      </div>

      {/* Add row */}
      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
          <div className="md:col-span-5">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Item</label>
            <select
              value={addKind}
              onChange={(e) => setAddKind(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            >
              <option value="">— pick an item —</option>
              {COMMON_KINDS.map(k => <option key={k.kind} value={k.kind}>{k.label}</option>)}
            </select>
          </div>
          {addKind === 'other' && (
            <div className="md:col-span-4">
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Describe</label>
              <input
                type="text"
                value={addCustom}
                onChange={(e) => setAddCustom(e.target.value)}
                placeholder="e.g. notarized signature"
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              />
            </div>
          )}
          <div className={addKind === 'other' ? 'md:col-span-2' : 'md:col-span-5'}>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Due by (optional)</label>
            <input
              type="date"
              value={addDueAt}
              onChange={(e) => setAddDueAt(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
            />
          </div>
          <div className="md:col-span-2">
            <button
              onClick={addRow}
              disabled={busy || !clientId || !addKind}
              className="w-full text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-2 disabled:opacity-50 inline-flex items-center justify-center gap-1"
            >
              <Plus size={12}/> Add
            </button>
          </div>
        </div>
      </div>

      {/* Rows */}
      {loading ? (
        <div className="text-xs text-slate-500 py-2">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-2 italic">No requirements logged. Add what you're waiting on from this client above.</div>
      ) : (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
          {pending.map(row => (
            <RequirementRow
              key={row.id}
              row={row}
              busy={busy}
              onComplete={() => setStatus(row, 'completed')}
              onWaive={() => setStatus(row, 'waived')}
              onToggleFollowup={() => toggleFollowup(row)}
              onDelete={() => deleteRow(row)}
            />
          ))}
          {completed.length > 0 && (
            <details className="px-3 py-2 bg-slate-50/50">
              <summary className="text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer">
                {completed.length} completed / waived
              </summary>
              <div className="mt-2 divide-y divide-slate-100">
                {completed.map(row => (
                  <RequirementRow
                    key={row.id}
                    row={row}
                    busy={busy}
                    onReopen={() => setStatus(row, 'pending')}
                    onDelete={() => deleteRow(row)}
                  />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function labelFor(row) {
  const match = COMMON_KINDS.find(k => k.kind === row.kind);
  if (match && row.kind !== 'other') return match.label;
  return (row.description && row.description.trim()) || row.kind.replace(/_/g, ' ');
}

function RequirementRow({ row, busy, onComplete, onWaive, onReopen, onToggleFollowup, onDelete }) {
  const isClosed = row.status !== 'pending';
  const isOverdue = !isClosed && row.due_at && Date.parse(row.due_at) < Date.now();
  const dueLabel = row.due_at ? new Date(row.due_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;

  return (
    <div className={`flex items-center gap-3 px-3 py-2 ${isClosed ? 'opacity-60' : ''}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 flex items-baseline gap-2 flex-wrap">
          <span>{labelFor(row)}</span>
          {isOverdue && <span className="text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800 border border-red-200 rounded-full px-1.5 py-0.5">Overdue</span>}
          {row.status === 'completed' && <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5">Done</span>}
          {row.status === 'waived'    && <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-1.5 py-0.5">Waived</span>}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {dueLabel && <span>Due {dueLabel} · </span>}
          {row.last_reminder_at
            ? <span>AI reminded {fmtAgo(row.last_reminder_at)} ({row.reminder_count}×)</span>
            : <span>No reminder sent yet</span>}
        </div>
        {row.notes && <div className="text-[11px] text-slate-600 mt-0.5 italic">{row.notes}</div>}
      </div>
      {!isClosed && (
        <button
          onClick={onToggleFollowup}
          disabled={busy}
          title={row.ai_followup_enabled ? 'AI follow-up is ON. Click to silence.' : 'AI follow-up is OFF. Click to enable.'}
          className={`text-slate-400 hover:text-slate-700 disabled:opacity-50 ${row.ai_followup_enabled ? 'text-emerald-600 hover:text-emerald-800' : ''}`}
        >
          {row.ai_followup_enabled ? <Bell size={14}/> : <BellOff size={14}/>}
        </button>
      )}
      {!isClosed && (
        <>
          <button
            onClick={onComplete}
            disabled={busy}
            title="Mark complete"
            className="text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
          >
            <Check size={16}/>
          </button>
          <button
            onClick={onWaive}
            disabled={busy}
            title="Waive (not needed)"
            className="text-slate-400 hover:text-slate-700 disabled:opacity-50"
          >
            <X size={14}/>
          </button>
        </>
      )}
      {isClosed && onReopen && (
        <button
          onClick={onReopen}
          disabled={busy}
          title="Reopen"
          className="text-slate-400 hover:text-slate-700 disabled:opacity-50"
        >
          <RotateCcw size={14}/>
        </button>
      )}
      <button
        onClick={onDelete}
        disabled={busy}
        title="Delete"
        className="text-slate-400 hover:text-red-600 disabled:opacity-50"
      >
        <Trash2 size={14}/>
      </button>
    </div>
  );
}
